import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { 
  CalendarDays, Plus, Edit2, Trash2, Loader2, RefreshCw, 
  Calendar, Check, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from '../ui/sonner';
import api from '../../lib/api';

const HARI_LIST = [
  { value: 'senin', label: 'Senin' },
  { value: 'selasa', label: 'Selasa' },
  { value: 'rabu', label: 'Rabu' },
  { value: 'kamis', label: 'Kamis' },
  { value: 'jumat', label: 'Jumat' },
  { value: 'sabtu', label: 'Sabtu' },
  { value: 'minggu', label: 'Minggu' }
];

const BULAN_LIST = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

const HariAktifPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hariAktif, setHariAktif] = useState([]);
  const [libur, setLibur] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Dialog states
  const [showHariAktifDialog, setShowHariAktifDialog] = useState(false);
  const [showLiburDialog, setShowLiburDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Calendar view state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  // Form states
  const [selectedHari, setSelectedHari] = useState(['senin', 'selasa', 'rabu', 'kamis', 'jumat']);
  const [liburForm, setLiburForm] = useState({
    tanggal: '',
    keterangan: '',
    jenis: 'nasional' // nasional, sekolah
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [hariRes, liburRes] = await Promise.all([
        api.get('/hari-aktif'),
        api.get('/libur')
      ]);
      setHariAktif(hariRes.data.hari_aktif || ['senin', 'selasa', 'rabu', 'kamis', 'jumat']);
      setSelectedHari(hariRes.data.hari_aktif || ['senin', 'selasa', 'rabu', 'kamis', 'jumat']);
      setLibur(liburRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      // Set default if API not ready
      setHariAktif(['senin', 'selasa', 'rabu', 'kamis', 'jumat']);
      setSelectedHari(['senin', 'selasa', 'rabu', 'kamis', 'jumat']);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHariAktif = async () => {
    try {
      setSubmitting(true);
      await api.put('/hari-aktif', { hari_aktif: selectedHari });
      setHariAktif(selectedHari);
      toast.success('Hari aktif berhasil disimpan');
      setShowHariAktifDialog(false);
    } catch (error) {
      toast.error('Gagal menyimpan hari aktif');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddLibur = async () => {
    try {
      if (!liburForm.tanggal || !liburForm.keterangan) {
        toast.error('Tanggal dan keterangan wajib diisi');
        return;
      }
      
      setSubmitting(true);
      await api.post('/libur', liburForm);
      toast.success('Hari libur berhasil ditambahkan');
      setShowLiburDialog(false);
      setLiburForm({ tanggal: '', keterangan: '', jenis: 'nasional' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan hari libur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLibur = async () => {
    try {
      setSubmitting(true);
      await api.delete(`/libur/${selectedItem.id}`);
      toast.success('Hari libur berhasil dihapus');
      setShowDeleteDialog(false);
      loadData();
    } catch (error) {
      toast.error('Gagal menghapus hari libur');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleHari = (hari) => {
    setSelectedHari(prev => 
      prev.includes(hari) 
        ? prev.filter(h => h !== hari)
        : [...prev, hari]
    );
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    const days = [];
    
    // Add empty cells for days before the first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayName = ['minggu', 'senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu'][date.getDay()];
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isLibur = libur.some(l => l.tanggal === dateStr);
      const isHariAktif = hariAktif.includes(dayName);
      const isToday = new Date().toDateString() === date.toDateString();
      
      days.push({
        day,
        date: dateStr,
        dayName,
        isLibur,
        liburInfo: libur.find(l => l.tanggal === dateStr),
        isHariAktif,
        isWeekend: dayName === 'sabtu' || dayName === 'minggu',
        isToday
      });
    }
    
    return days;
  };

  const canEdit = user?.role === 'admin_dinas';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="hari-aktif-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-indigo-950">Hari Aktif & Libur</h1>
          <p className="text-gray-500">Kelola jadwal hari kerja dan hari libur sekolah</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadData} className="border-indigo-200">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Hari Aktif Card */}
        <Card className="border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                Hari Aktif Sekolah
              </div>
              {canEdit && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setShowHariAktifDialog(true)}
                  className="border-indigo-200"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">
              Guru hanya perlu absensi pada hari-hari berikut:
            </p>
            <div className="flex flex-wrap gap-2">
              {HARI_LIST.map(hari => (
                <Badge 
                  key={hari.value}
                  className={hariAktif.includes(hari.value) 
                    ? 'bg-emerald-100 text-emerald-800' 
                    : 'bg-gray-100 text-gray-500'
                  }
                >
                  {hariAktif.includes(hari.value) ? (
                    <Check className="w-3 h-3 mr-1" />
                  ) : (
                    <X className="w-3 h-3 mr-1" />
                  )}
                  {hari.label}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Holidays */}
        <Card className="border-indigo-100/50 lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-600" />
                Hari Libur
              </div>
              {canEdit && (
                <Button 
                  size="sm" 
                  onClick={() => setShowLiburDialog(true)}
                  className="bg-indigo-950 hover:bg-indigo-900"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Tambah Libur
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {libur.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Belum ada hari libur yang ditambahkan</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-auto">
                {libur
                  .sort((a, b) => new Date(a.tanggal) - new Date(b.tanggal))
                  .map(item => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-[50px]">
                          <p className="text-2xl font-bold text-red-600">
                            {new Date(item.tanggal).getDate()}
                          </p>
                          <p className="text-xs text-red-500">
                            {BULAN_LIST[new Date(item.tanggal).getMonth()].slice(0, 3)}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-red-800">{item.keterangan}</p>
                          <Badge className={item.jenis === 'nasional' 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-amber-100 text-amber-700'
                          }>
                            {item.jenis === 'nasional' ? 'Libur Nasional' : 'Libur Sekolah'}
                          </Badge>
                        </div>
                      </div>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedItem(item);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Calendar View */}
      <Card className="border-indigo-100/50 mt-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Kalender
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  if (currentMonth === 0) {
                    setCurrentMonth(11);
                    setCurrentYear(currentYear - 1);
                  } else {
                    setCurrentMonth(currentMonth - 1);
                  }
                }}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <span className="font-medium min-w-[150px] text-center">
                {BULAN_LIST[currentMonth]} {currentYear}
              </span>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  if (currentMonth === 11) {
                    setCurrentMonth(0);
                    setCurrentYear(currentYear + 1);
                  } else {
                    setCurrentMonth(currentMonth + 1);
                  }
                }}
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map((dayInfo, idx) => (
              <div 
                key={idx}
                className={`
                  min-h-[60px] p-1 rounded-lg text-center relative
                  ${!dayInfo ? 'bg-transparent' : ''}
                  ${dayInfo?.isToday ? 'ring-2 ring-indigo-500' : ''}
                  ${dayInfo?.isLibur ? 'bg-red-100' : ''}
                  ${dayInfo?.isWeekend && !dayInfo?.isLibur ? 'bg-gray-100' : ''}
                  ${dayInfo?.isHariAktif && !dayInfo?.isLibur && !dayInfo?.isWeekend ? 'bg-emerald-50' : ''}
                  ${!dayInfo?.isHariAktif && !dayInfo?.isLibur && !dayInfo?.isWeekend && dayInfo ? 'bg-gray-50' : ''}
                `}
              >
                {dayInfo && (
                  <>
                    <span className={`
                      text-sm font-medium
                      ${dayInfo.isLibur ? 'text-red-600' : ''}
                      ${dayInfo.isWeekend && !dayInfo.isLibur ? 'text-gray-400' : ''}
                      ${dayInfo.isToday ? 'text-indigo-600' : ''}
                    `}>
                      {dayInfo.day}
                    </span>
                    {dayInfo.isLibur && (
                      <div className="absolute bottom-1 left-1 right-1">
                        <p className="text-[10px] text-red-600 truncate" title={dayInfo.liburInfo?.keterangan}>
                          {dayInfo.liburInfo?.keterangan}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-50 border border-emerald-200"></div>
              <span className="text-sm text-gray-600">Hari Aktif</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-100 border border-gray-200"></div>
              <span className="text-sm text-gray-600">Hari Libur Mingguan</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
              <span className="text-sm text-gray-600">Hari Libur</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Hari Aktif Dialog */}
      <Dialog open={showHariAktifDialog} onOpenChange={setShowHariAktifDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Hari Aktif Sekolah</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-4">
              Pilih hari-hari dimana guru wajib melakukan absensi:
            </p>
            <div className="space-y-3">
              {HARI_LIST.map(hari => (
                <label 
                  key={hari.value}
                  className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <Checkbox
                    checked={selectedHari.includes(hari.value)}
                    onCheckedChange={() => toggleHari(hari.value)}
                  />
                  <span className="font-medium">{hari.label}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHariAktifDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleSaveHariAktif}
              disabled={submitting || selectedHari.length === 0}
              className="bg-indigo-950 hover:bg-indigo-900"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Libur Dialog */}
      <Dialog open={showLiburDialog} onOpenChange={setShowLiburDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tambah Hari Libur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tanggal</Label>
              <Input
                type="date"
                value={liburForm.tanggal}
                onChange={(e) => setLiburForm(prev => ({ ...prev, tanggal: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Keterangan</Label>
              <Input
                placeholder="Contoh: Hari Raya Idul Fitri"
                value={liburForm.keterangan}
                onChange={(e) => setLiburForm(prev => ({ ...prev, keterangan: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Jenis Libur</Label>
              <Select 
                value={liburForm.jenis} 
                onValueChange={(v) => setLiburForm(prev => ({ ...prev, jenis: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nasional">Libur Nasional</SelectItem>
                  <SelectItem value="sekolah">Libur Sekolah</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLiburDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleAddLibur}
              disabled={submitting}
              className="bg-indigo-950 hover:bg-indigo-900"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Hapus Hari Libur</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Apakah Anda yakin ingin menghapus hari libur <strong>{selectedItem?.keterangan}</strong>?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Batal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteLibur}
              disabled={submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HariAktifPage;
